import React from "react";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { ClipboardList, CheckCircle2, Circle } from "lucide-react";

export default function PortalProjects() {
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      const r = await api.get("/portal/me/projects");
      setProjects(r.data);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="portal-projects-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Delivery</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Projects</h1>
        <p className="text-slate-500 text-sm mt-1">Implementation, onboarding and ongoing projects Xentra is running for you.</p>
      </div>

      {loading ? (
        <div className="text-slate-500 font-mono text-sm">Loading…</div>
      ) : projects.length === 0 ? (
        <Card className="border-slate-200 p-12 text-center">
          <ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">No projects yet</div>
          <div className="text-sm text-slate-500 mt-1">When Xentra kicks off a new piece of work, it'll appear here.</div>
        </Card>
      ) : (
        <div className="space-y-4">
          {projects.map((p) => {
            const steps = p.steps || [];
            const done = steps.filter((s) => s.done).length;
            const pct = steps.length ? Math.round((done / steps.length) * 100) : 0;
            return (
              <Card key={p.id} className="border-slate-200" data-testid={`portal-project-${p.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="font-heading text-lg font-bold text-slate-900">{p.name}</div>
                      {p.description && <div className="text-sm text-slate-500 mt-1">{p.description}</div>}
                      <div className="text-xs text-slate-500 mt-2 font-mono">
                        Status · <span className="text-slate-800">{p.status || "In Progress"}</span>
                        {p.target_completion && <> · Target {fmtDate(p.target_completion)}</>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-2xl font-bold text-emerald-700">{pct}%</div>
                      <div className="text-xs text-slate-500 font-mono">{done}/{steps.length} steps</div>
                    </div>
                  </div>
                  <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                  {steps.length > 0 && (
                    <div className="mt-4 space-y-1.5">
                      {steps.map((s, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          {s.done ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          ) : (
                            <Circle className="h-4 w-4 text-slate-300 flex-shrink-0" />
                          )}
                          <span className={s.done ? "text-slate-500 line-through" : "text-slate-700"}>{s.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
